import React, { useState, useRef } from 'react';
import { array, func, string, number, bool } from 'prop-types';
import { noop, toLower } from 'lodash';
import cn from 'classnames';
import ReactTagsInput from 'react-tagsinput';
import { REMOVE_TAGS_KEYCODES } from '../constants';
import AutocompleteInput from './AutocompleteInput';
import { ADD_TAGS_KEYCODES, DEFAULT_MAX_TAGS } from './constants';
import { isEmptyTag } from './utils';
import './autosuggestTheme.scss';
import './style.scss';

const TagsInput = ({
    tags = [],
    onChange = noop,
    placeholder,
    className = '',
    maxTags = DEFAULT_MAX_TAGS,
    suggestions = [],
    validationRegex = /.*/,
    suggestionsOnly = false,
    lowercaseTags = false,
    showSuggestionsOnFocus = false,
    addOnBlur = false,
    hideTags = false,
}) => {
    const inputRef = useRef();

    const [inputValue, setInputValue] = useState('');

    const handleTagChange = (tags, changed = []) => {
        if (suggestionsOnly && !suggestions.includes(changed[0])) {
            return;
        }

        if (isEmptyTag(changed[0])) {
            return;
        }

        onChange(lowercaseTags ? tags.map(toLower) : tags);
        setInputValue('');

        if (maxTags === tags.length) {
            // This makes sure the suggestions list closes.
            inputRef.current.blur();
        }
    };

    const handleInputChange = (tag = '') => {
        if (!tag.match(validationRegex)) {
            return;
        }
        setInputValue(tag);
    };

    const autocompleteRenderInput = (props) => (
        <AutocompleteInput
            showSuggestionsOnFocus={showSuggestionsOnFocus}
            suggestions={suggestions}
            inputRef={inputRef}
            tags={tags}
            {...props}
        />
    );

    const tagsInputProps = {
        placeholder: hideTags ? placeholder : !tags.length && placeholder,
        disabled: hideTags && tags.length >= maxTags,
        hidden: !hideTags && tags.length >= maxTags,
    };

    return (
        <ReactTagsInput
            onChange={handleTagChange}
            ref={inputRef}
            onlyUnique={true}
            value={tags}
            className={cn('react-tagsinput', className)}
            inputValue={inputValue}
            addKeys={Object.values(ADD_TAGS_KEYCODES)}
            onChangeInput={handleInputChange}
            addOnBlur={addOnBlur}
            renderInput={autocompleteRenderInput}
            inputProps={tagsInputProps}
            renderTag={hideTags ? noop : undefined}
            removeKeys={hideTags ? [] : REMOVE_TAGS_KEYCODES}
            tagProps={{
                className: 'tag',
                classNameRemove: 'tag-remove',
                getTagDisplayValue: (tag) => tag,
            }}
            maxTags={maxTags}
        />
    );
};

TagsInput.propTypes = {
    tags: array,
    onChange: func,
    placeholder: string,
    className: string,
    maxTags: number,
    suggestions: array,
    validationRegex: string,
    suggestionsOnly: bool,
    lowercaseTags: bool,
    showSuggestionsOnFocus: bool,
    addOnBlur: bool,
    hideTags: bool,
};

export default TagsInput;
